<template>
  <div class="mx-auto pt-8 text-center text-black w-fit">
    <h3 class="text-base font-medium">Join Break Moods</h3>
    <h1 class="text-4xl font-bold md:text-5xl">Sound Engineer</h1>
    <p class="font-light mt-1">Start your journey with us today</p>
  </div>
  <div class="px-0 sm:px-8" v-if="currentStep < 3">
    <FirstForm v-if="currentStep == 1" :initial-values="form" @next="handleForm" />
    <SecondForm
      v-if="currentStep == 2"
      @back="backStep"
      @next="onSubmit"
      :loading="loading"
    />
  </div>

  <ViewProgressComponent
    v-if="upLoading.isUploading"
    :phase="upLoading.phase"
    :files="upLoading.files"
    role="SoundEngineer"
  />

  <div class="sm:h-[calc(100vh-13rem)]" v-if="currentStep == 3">
    <FinalForm />
  </div>
  <MessageComponent />
</template>

<script setup>
/* eslint-disable */
import { ref } from "vue";
import FirstForm from "./FirstForm.vue";
import SecondForm from "./SecondForm.vue";
import FinalForm from "@/shared/component/FinalForm.vue";
import MessageComponent from "@/shared/component/MessageComponent.vue";
import { useMessage } from "@/shared/services/messageServices";
import axios from "axios";
import ViewProgressComponent from "@/shared/component/ViewProgressComponent.vue";

const currentStep = ref(1);
const loading = ref(false);
const upLoading = ref({
  isUploading: false,
  files: [],
  phase: "upload",
});

const messagesService = useMessage();

const form = ref({
  role: "SoundEngineer",
  fullName: "",
  email: "",
  contactNumber: "",
  location: "",
  yearsExperience: "",
  dawsUsed: "",
  portfolioLinks: [],
  beforeFile1: null,
  beforeFile1Key: null,
  afterFile1: null,
  afterFile1Key: null,
  description1: "",
  beforeFile2: null,
  beforeFile2Key: null,
  afterFile2: null,
  afterFile2Key: null,
  description2: "",
  availability: "",
  internetConnection: false,
  hardwareSoftwareSetup: false,
  exclusiveSubmissionAgreement: false,
});

function handleForm(data) {
  form.value = { ...form.value, ...data };
  if (currentStep.value < 2) currentStep.value++;
}

async function uploadFileAndGetKey(file, fileIndex) {
  if (!file) return null;

  // Add file to the files array for progress tracking
  const fileItem = {
    id: fileIndex,
    name: file.name,
    progress: 0,
    dim: file.size,
  };

  const fileItemIndex = upLoading.value.files.findIndex((f) => f.id === fileIndex);
  if (fileItemIndex === -1) {
    upLoading.value.files.push(fileItem);
  } else {
    upLoading.value.files[fileItemIndex] = fileItem;
  }

  try {
    // Get pre-signed URL
    const response = await axios.post(
      `${process.env.VUE_APP_ENDPOINT_API}/work-with-us/getPreSignedUrl`,
      {
        fileName: file.name,
        fileType: file.type,
        role: "SoundEngineer",
      },
      { headers: { "Content-Type": "application/json" } }
    );

    const signedUrl = response.data.signedUrl;

    const key = response.data.key;

    // Create XMLHttpRequest to track progress
    const xhr = new XMLHttpRequest();

    // Setup promise to handle upload completion
    const uploadPromise = new Promise((resolve, reject) => {
      xhr.open("PUT", signedUrl);
      xhr.setRequestHeader("Content-Type", file.type);

      // Track upload progress
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          // Update the file progress in the files array
          const index = upLoading.value.files.findIndex((f) => f.id === fileIndex);
          if (index !== -1) {
            upLoading.value.files[index].progress = percentComplete;
          }
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          console.error("Upload failed:", xhr.status, xhr.statusText);
          reject(new Error(`HTTP Error: ${xhr.status}`));
        }
      };

      xhr.onerror = () => reject(new Error('Network Error'));
      xhr.send(file);
    });

    // Wait for upload to complete
    await uploadPromise;

    // Set progress to 100% when complete
    const index = upLoading.value.files.findIndex((f) => f.id === fileIndex);
    if (index !== -1) {
      upLoading.value.files[index].progress = 100;
    }

    return key;
  } catch (error) {
    console.error(`Error uploading file ${file.name}:`, error);
    throw error;
  }
}

async function onSubmit(data) {
  loading.value = true;
  handleForm(data);

  try {
    // Initialize upload process
    upLoading.value.isUploading = true;
    upLoading.value.phase = "upload";
    upLoading.value.files = [];

    // Array to collect promises for all uploads
    const uploadPromises = [];

    // Upload beforeFile1 if it exists
    if (form.value.beforeFile1) {
      const beforeFile1Promise = uploadFileAndGetKey(form.value.beforeFile1, 1).then(
        (key) => {
          form.value.beforeFile1Key = key;
        }
      );
      uploadPromises.push(beforeFile1Promise);
    }

    // Upload afterFile1 if it exists
    if (form.value.afterFile1) {
      const afterFile1Promise = uploadFileAndGetKey(form.value.afterFile1, 2).then(
        (key) => {
          form.value.afterFile1Key = key;
        }
      );
      uploadPromises.push(afterFile1Promise);
    }

    // Upload beforeFile2 if it exists
    if (form.value.beforeFile2) {
      const beforeFile2Promise = uploadFileAndGetKey(form.value.beforeFile2, 3).then(
        (key) => {
          form.value.beforeFile2Key = key;
        }
      );
      uploadPromises.push(beforeFile2Promise);
    }

    // Upload afterFile2 if it exists
    if (form.value.afterFile2) {
      const afterFile2Promise = uploadFileAndGetKey(form.value.afterFile2, 4).then(
        (key) => {
          form.value.afterFile2Key = key;
        }
      );
      uploadPromises.push(afterFile2Promise);
    }

    // Wait for all uploads to complete
    await Promise.all(uploadPromises);

    // Switch to form submission phase
    upLoading.value.phase = "submission";

    // Create formData without original file objects, but with keys
    const { 
      beforeFile1, 
      afterFile1, 
      beforeFile2, 
      afterFile2, 
      ...formDataToSend 
    } = form.value;

    // Submit form
    await axios.post(
      `${process.env.VUE_APP_ENDPOINT_API}/work-with-us/form`,
      formDataToSend,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );

    currentStep.value++;
  } catch (error) {
    console.error(
      "Error Status:",
      error.response?.status,
      "- Error Message: ",
      error.response?.data?.error || error.message
    );
    messagesService.error(
      error.response?.data?.error || "An error occurred during submission"
    );
  } finally {
    loading.value = false;
    upLoading.value.isUploading = false;
  }
}

function backStep() {
  if (currentStep.value > 0) currentStep.value--;
}
</script>
